import React, { useState } from "react"
import { useMutation } from "urql"

import { useManualBooking } from "../../contexts/ManualBookingContext"
import { useToast } from "../../contexts/ToastContext"
import { formatShortDateTime } from "../../utils/utils"
import { UPDATE_APPOINTMENT_V2_MUTATION } from "../pro_portal/calendar/appointment_graphql"
import CalendarOverrideConfirmModal from "../pro_portal/calendar/CalendarOverrideConfirmModal"

import { TextArea } from "./Inputs"
import { AnimatedModal } from "./Modal"

export default function ConfirmRescheduleModal({ showReschedule, hideModal, onRescheduleSuccess }) {
  const [practitionerNote, setPractitionerNote] = useState()
  const [loading, setLoading] = useState(false)
  const [showCalendarOverride, setShowCalendarOverride] = useState(false)
  const [overrideVariant, setOverrideVariant] = useState("unreachable")
  const { appointment, rescheduledStartsAt, rescheduledEndsAt, locationId } = useManualBooking()
  const [, updateAppointmentV2] = useMutation(UPDATE_APPOINTMENT_V2_MUTATION)
  const { showToast } = useToast()

  if (!appointment) {
    return null
  }

  const clientName = appointment.client?.firstName || appointment.user?.firstName || "your client"

  const submitReschedule = ({ acknowledgeCalendarOverride = false } = {}) => {
    setLoading(true)
    updateAppointmentV2({
      appointmentId: appointment.id,
      locationId,
      startsAt: rescheduledStartsAt,
      endsAt: rescheduledEndsAt,
      practitionerNote,
      acknowledgeCalendarOverride
    }).then((result) => {
      setLoading(false)
      const payload = result.data?.updateAppointmentV2
      if (payload?.result === "success") {
        setShowCalendarOverride(false)
        showToast("Appointment rescheduled successfully. ")
        if (payload.appointment) {
          onRescheduleSuccess(appointment.id, payload.appointment)
        }
        hideModal()
      } else if (payload?.result === "override_required") {
        setOverrideVariant(payload.overrideReason === "conflict" ? "conflict" : "unreachable")
        setShowCalendarOverride(true)
      } else {
        console.error(result) // eslint-disable-line no-console
        const errorMessage = payload?.errors?.join() ?? "unknown error"
        showToast({
          content: `We weren't able to reschedule this appointment: ${errorMessage}`,
          type: "error"
        })
        setShowCalendarOverride(false)
        hideModal()
      }
    })
  }

  return (
    <>
      <AnimatedModal
        visible={showReschedule && !showCalendarOverride}
        hideModal={hideModal}
        header="Reschedule session now"
        actionButtonCopy={loading ? "Rescheduling..." : "Reschedule"}
        saveDisabled={loading}
        showFooter={true}
        onSave={() => submitReschedule()}>
        <p className="mb-4">
          This will reschedule {clientName}&apos;s session from:
          <br />
          {formatShortDateTime(appointment.startsAt)} - {formatShortDateTime(appointment.endsAt)}
          <br />
          to:
          <br />
          {formatShortDateTime(rescheduledStartsAt)} - {formatShortDateTime(rescheduledEndsAt)}.
          <br />
          <br />
          Add a message below if you want to send an email to {clientName}:
        </p>
        <TextArea
          placeholder="Type in your message here..."
          rows="4"
          onChange={(e) => setPractitionerNote(e.target.value)}
          value={practitionerNote}
        />
      </AnimatedModal>
      <CalendarOverrideConfirmModal
        visible={showCalendarOverride}
        hideModal={() => {
          setShowCalendarOverride(false)
          hideModal()
        }}
        action="reschedule"
        variant={overrideVariant}
        disabled={loading}
        onConfirm={() => submitReschedule({ acknowledgeCalendarOverride: true })}
      />
    </>
  )
}
